import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Instagram, Globe, ChevronDown, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBooking } from '../contexts/BookingContext';
import { Button } from './ui/button';
import BookingModal from './BookingModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-wellness-spot/artifacts/y6uqcyxn_1683577696901.png";
const BOOKING_URL = "https://pt.zappysoftware.com/m/iprbckamit";

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, language, changeLanguage } = useLanguage();
  const { openBooking } = useBooking();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/services', label: t('nav_services') },
    { path: '/afro-hair', label: t('nav_afro') },
    { path: '/spa-wellness', label: t('nav_spa') },
    { path: '/gallery', label: t('nav_gallery') },
    { path: '/store', label: t('nav_store') },
    { path: '/promotions', label: language === 'fr' ? 'Promos' : language === 'pt' ? 'Promoções' : 'Promos' },
    { path: '/training', label: t('nav_training') },
    { path: '/about', label: t('nav_about') },
    { path: '/contact', label: t('nav_contact') },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-kamit-beige/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="IPRBC KAMIT" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm uppercase tracking-widest transition-colors relative
                  ${location.pathname === link.path 
                    ? 'text-kamit-gold' 
                    : 'text-kamit-brown hover:text-kamit-gold'
                  }
                  after:content-[''] after:absolute after:bottom-[-4px] after:left-0 
                  after:w-0 after:h-[1px] after:bg-kamit-gold hover:after:w-full after:transition-all
                `}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side - Language & Book Button */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-2 text-kamit-brown"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span>{currentLang.flag}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-kamit-beige border-kamit-brown/20">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-kamit-gold/20' : ''}`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book Button */}
            <Button 
              onClick={() => openBooking()}
              className="bg-kamit-brown hover:bg-kamit-dark text-kamit-beige rounded-full px-6 btn-shine"
              data-testid="book-now-btn"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t('nav_book')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-kamit-brown"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-kamit-brown/95 backdrop-blur-md border-t border-kamit-gold/20 py-4"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm uppercase tracking-widest transition-colors
                    ${location.pathname === link.path 
                      ? 'text-kamit-gold bg-kamit-beige/10' 
                      : 'text-kamit-beige hover:text-kamit-gold hover:bg-kamit-beige/10'
                    }
                  `}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Language Selector */}
              <div className="px-4 py-3 flex items-center gap-2">
                <Globe className="h-4 w-4 text-kamit-gold" />
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-2 py-1 text-sm rounded ${
                        language === lang.code 
                          ? 'bg-kamit-gold text-kamit-dark' 
                          : 'text-kamit-beige hover:bg-kamit-beige/20'
                      }`}
                    >
                      {lang.flag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Book Button */}
              <div className="px-4 pt-2">
                <Button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openBooking();
                  }}
                  className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {t('nav_book')}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer className="bg-kamit-dark text-kamit-beige" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <img 
              src={LOGO_URL} 
              alt="IPRBC KAMIT" 
              className="h-24 w-auto object-contain"
            />
            <p 
              className="text-kamit-beige/90 text-xl md:text-2xl leading-relaxed"
              style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
            >
              Instituts Professionnels de Relaxations et de Beautés Corporelles
            </p>
            <p 
              className="text-kamit-gold italic text-lg"
              style={{ fontFamily: "'Tangerine', cursive", fontWeight: 400 }}
            >
              &ldquo;Votre fontaine de jouvence&rdquo;
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-kamit-gold">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/services" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{t('nav_services')}</Link></li>
              <li><Link to="/afro-hair" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{t('nav_afro')}</Link></li>
              <li><Link to="/spa-wellness" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{t('nav_spa')}</Link></li>
              <li><Link to="/store" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{t('nav_store')}</Link></li>
              <li><Link to="/training" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{t('nav_training')}</Link></li>
              <li><Link to="/promotions" className="text-kamit-beige/70 hover:text-kamit-gold transition-colors">{language === 'fr' ? 'Promotions' : 'Promotions'}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-kamit-gold">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-kamit-gold shrink-0 mt-0.5" />
                <span className="text-kamit-beige/70 text-sm">
                  Rua da Ponte Nova N° 27<br />
                  Loja A, 9050-440<br />
                  Santa Luzia, Madeira
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-kamit-gold" />
                <div className="text-kamit-beige/70 text-sm">
                  <a href="tel:+351935984047" className="hover:text-kamit-gold">935 984 047</a>
                  <span className="mx-2">/</span>
                  <a href="tel:+351291650959" className="hover:text-kamit-gold">291 650 959</a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-kamit-gold" />
                <a href="mailto:iprbckamit@gmail.com" className="text-kamit-beige/70 text-sm hover:text-kamit-gold">
                  iprbckamit@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="h-5 w-5 text-kamit-gold" />
                <a href="https://instagram.com/kamitmadeira" target="_blank" rel="noopener noreferrer" className="text-kamit-beige/70 text-sm hover:text-kamit-gold">
                  @kamitmadeira
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-kamit-gold">
              {language === 'fr' ? 'Horaires' : language === 'de' ? 'Öffnungszeiten' : language === 'pt' ? 'Horário' : language === 'es' ? 'Horario' : 'Hours'}
            </h4>
            <ul className="space-y-1 text-kamit-beige/70 text-sm">
              <li className="flex justify-between">
                <span>{language === 'fr' ? 'Lundi' : language === 'de' ? 'Montag' : language === 'pt' ? 'Segunda' : language === 'es' ? 'Lunes' : 'Monday'}</span>
                <span>9:00 - 20:00</span>
              </li>
              <li className="flex justify-between">
                <span>{language === 'fr' ? 'Mardi' : language === 'de' ? 'Dienstag' : language === 'pt' ? 'Terça' : language === 'es' ? 'Martes' : 'Tuesday'}</span>
                <span>9:00 - 20:00</span>
              </li>
              <li className="flex justify-between">
                <span>{language === 'fr' ? 'Mercredi' : language === 'de' ? 'Mittwoch' : language === 'pt' ? 'Quarta' : language === 'es' ? 'Miércoles' : 'Wednesday'}</span>
                <span>9:00 - 20:00</span>
              </li>
              <li className="flex justify-between text-kamit-beige/50">
                <span>{language === 'fr' ? 'Jeudi' : language === 'de' ? 'Donnerstag' : language === 'pt' ? 'Quinta' : language === 'es' ? 'Jueves' : 'Thursday'}</span>
                <span>{language === 'fr' ? 'Fermé' : language === 'de' ? 'Geschlossen' : language === 'pt' ? 'Fechado' : language === 'es' ? 'Cerrado' : 'Closed'}</span>
              </li>
              <li className="flex justify-between">
                <span>{language === 'fr' ? 'Vendredi' : language === 'de' ? 'Freitag' : language === 'pt' ? 'Sexta' : language === 'es' ? 'Viernes' : 'Friday'}</span>
                <span>9:00 - 20:00</span>
              </li>
              <li className="flex justify-between">
                <span>{language === 'fr' ? 'Samedi' : language === 'de' ? 'Samstag' : language === 'pt' ? 'Sábado' : language === 'es' ? 'Sábado' : 'Saturday'}</span>
                <span>9:00 - 20:00</span>
              </li>
              <li className="flex justify-between text-kamit-beige/50">
                <span>{language === 'fr' ? 'Dimanche' : language === 'de' ? 'Sonntag' : language === 'pt' ? 'Domingo' : language === 'es' ? 'Domingo' : 'Sunday'}</span>
                <span>{language === 'fr' ? 'Fermé' : language === 'de' ? 'Geschlossen' : language === 'pt' ? 'Fechado' : language === 'es' ? 'Cerrado' : 'Closed'}</span>
              </li>
              <li className="flex justify-between text-kamit-gold mt-2 pt-2 border-t border-kamit-beige/20">
                <span>{language === 'fr' ? 'Jours fériés' : language === 'de' ? 'Feiertage' : language === 'pt' ? 'Feriados' : language === 'es' ? 'Festivos' : 'Holidays'}</span>
                <span>{language === 'fr' ? 'Ouvert' : language === 'de' ? 'Geöffnet' : language === 'pt' ? 'Aberto' : language === 'es' ? 'Abierto' : 'Open'}</span>
              </li>
            </ul>
            
            <a 
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6"
            >
              <Button className="bg-kamit-gold hover:bg-kamit-gold/80 text-kamit-dark rounded-full">
                {t('nav_book')}
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-kamit-beige/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-kamit-beige/50 text-sm">
              © {new Date().getFullYear()} IPRBC KAMIT. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <p className="text-kamit-beige/50 text-sm">
                Delocalisation & Transfer Service Available
              </p>
              <Link 
                to="/admin" 
                className="bg-kamit-gold/20 hover:bg-kamit-gold/40 text-kamit-beige px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 border border-kamit-gold/30"
                data-testid="admin-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const { isBookingOpen, closeBooking, preselectedCategory, preselectedService } = useBooking();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      
      {/* Global Booking Modal */}
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={closeBooking}
        preselectedCategory={preselectedCategory}
        preselectedService={preselectedService}
      />
    </div>
  );
};

export default Layout;
